import React from "react";
import Markdown, { type Components } from "react-markdown";
import remarkGfm from "remark-gfm";
import remarkBreaks from "remark-breaks";
import rehypeHighlight from "rehype-highlight";
import ClassNames from "classnames";
import PreCodeBlock from "./Components/PreCodeBlock";
import MarkdownTable from "./Components/MarkdownTable";
import { type TableActions } from "./tableUtils";
import "./SeemMarkdown.scss";

export interface SeemMarkdownProps {
  children?: string;
  className?: string;
  tableActions?: TableActions;
  /** Stamped on every rendered link as a `user_click`. */
  linkAnalyticsProps?: Record<string, unknown>;
}

const SeemMarkdown: React.FC<SeemMarkdownProps> = ({
  children,
  className,
  tableActions,
  linkAnalyticsProps,
}) => {
  const markdownClassName = ClassNames("seem-markdown", className);

  // The hast node carries its own className for some elements (GFM task lists
  // add `contains-task-list` / `task-list-item`, footnotes add classes to `a`,
  // rehype-highlight adds `hljs language-*` to `code`). Merge instead of
  // overwriting, otherwise those nodes silently lose their markdown class.
  const withNodeClass = (nodeClassName?: string) =>
    ClassNames(markdownClassName, nodeClassName);

  const components: Components = {
    h1: ({ node: _node, className: nodeClassName, ...rest }) => (
      <h1 className={withNodeClass(nodeClassName)} {...rest} />
    ),
    h2: ({ node: _node, className: nodeClassName, ...rest }) => (
      <h2 className={withNodeClass(nodeClassName)} {...rest} />
    ),
    h3: ({ node: _node, className: nodeClassName, ...rest }) => (
      <h3 className={withNodeClass(nodeClassName)} {...rest} />
    ),
    h4: ({ node: _node, className: nodeClassName, ...rest }) => (
      <h4 className={withNodeClass(nodeClassName)} {...rest} />
    ),
    h5: ({ node: _node, className: nodeClassName, ...rest }) => (
      <h5 className={withNodeClass(nodeClassName)} {...rest} />
    ),
    h6: ({ node: _node, className: nodeClassName, ...rest }) => (
      <h6 className={withNodeClass(nodeClassName)} {...rest} />
    ),
    pre: ({ node: _node, className: nodeClassName, ...rest }) => (
      <PreCodeBlock className={withNodeClass(nodeClassName)} {...rest} />
    ),
    code: ({ node: _node, className: nodeClassName, ...rest }) => (
      <code className={withNodeClass(nodeClassName)} {...rest} />
    ),
    blockquote: ({ node: _node, className: nodeClassName, ...rest }) => (
      <blockquote className={withNodeClass(nodeClassName)} {...rest} />
    ),
    ul: ({ node: _node, className: nodeClassName, ...rest }) => (
      <ul className={withNodeClass(nodeClassName)} {...rest} />
    ),
    ol: ({ node: _node, className: nodeClassName, ...rest }) => (
      <ol className={withNodeClass(nodeClassName)} {...rest} />
    ),
    li: ({ node: _node, className: nodeClassName, ...rest }) => (
      <li className={withNodeClass(nodeClassName)} {...rest} />
    ),
    table: ({ node: _node, className: nodeClassName, ...rest }) => (
      <MarkdownTable
        className={withNodeClass(nodeClassName)}
        tableActions={tableActions}
        {...rest}
      />
    ),
    tr: ({ node: _node, className: nodeClassName, ...rest }) => (
      <tr className={withNodeClass(nodeClassName)} {...rest} />
    ),
    td: ({ node: _node, className: nodeClassName, ...rest }) => (
      <td className={withNodeClass(nodeClassName)} {...rest} />
    ),
    th: ({ node: _node, className: nodeClassName, ...rest }) => (
      <th className={withNodeClass(nodeClassName)} {...rest} />
    ),
    p: ({ node: _node, className: nodeClassName, ...rest }) => (
      <p className={withNodeClass(nodeClassName)} {...rest} />
    ),
    a: ({ node: _node, className: nodeClassName, ...rest }) => (
      <a
        className={withNodeClass(nodeClassName)}
        target="_blank"
        rel="noopener noreferrer"
        {...(linkAnalyticsProps && {
          "data-analytics-event": "user_click",
          "data-analytics-props": JSON.stringify(linkAnalyticsProps),
        })}
        {...rest}
      />
    ),
    em: ({ node: _node, className: nodeClassName, ...rest }) => (
      <em className={withNodeClass(nodeClassName)} {...rest} />
    ),
    strong: ({ node: _node, className: nodeClassName, ...rest }) => (
      <strong className={withNodeClass(nodeClassName)} {...rest} />
    ),
    hr: ({ node: _node, className: nodeClassName, ...rest }) => (
      <hr className={withNodeClass(nodeClassName)} {...rest} />
    ),
    del: ({ node: _node, className: nodeClassName, ...rest }) => (
      <del className={withNodeClass(nodeClassName)} {...rest} />
    ),
  };

  const cleanChildren = children?.replace(/\\n/g, "\n").replace(/\\t/g, "\t");

  return (
    <Markdown
      components={components}
      remarkPlugins={[remarkGfm, remarkBreaks]}
      rehypePlugins={[
        [
          rehypeHighlight,
          {
            // ignoreMissing prevents rehype-highlight from throwing on unrecognized
            // languages, which would break the <pre><code> structure entirely.
            ignoreMissing: true,
            languages: {
              // Custom diff language overrides highlight.js's default, which marks
              // both --- and +++ file headers as hljs-comment (same gray color).
              // This gives --- lines hljs-deletion (red) and +++ lines hljs-addition
              // (green), matching their semantic meaning. The -3/+3 rules must come
              // before the single -/+ rules so file headers don't get caught first
              // by the line-level deletion/addition patterns.
              diff: () => ({
                name: "diff",
                contains: [
                  {
                    className: "meta",
                    match: /^@@ +-\d+(?:,\d+)? +\+\d+(?:,\d+)? +@@/,
                  },
                  { className: "deletion", begin: /^-{3}/, end: /$/ },
                  { className: "addition", begin: /^\+{3}/, end: /$/ },
                  { className: "deletion", begin: /^-/, end: /$/ },
                  { className: "addition", begin: /^\+/, end: /$/ },
                ],
              }),
            },
          },
        ],
      ]}
    >
      {cleanChildren}
    </Markdown>
  );
};

export default SeemMarkdown;
